import { useEffect, useRef } from "react";

/**
 * ParticlesGold — canvas golden particles background for the final CTA.
 * Lightweight, no external dep (<100 lines).
 */
export const ParticlesGold = ({ density = 70, className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let particles = [];
    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const rand = (min, max) => Math.random() * (max - min) + min;
    particles = Array.from({ length: density }, () => ({
      x: rand(0, w),
      y: rand(0, h),
      r: rand(0.6, 2.2),
      vx: rand(-0.12, 0.12),
      vy: rand(-0.25, -0.05),
      o: rand(0.15, 0.8),
    }));

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -5) { p.y = h + 5; p.x = rand(0, w); }
        if (p.x < -5) p.x = w + 5;
        if (p.x > w + 5) p.x = -5;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
        grad.addColorStop(0, `rgba(232, 182, 92, ${p.o})`);
        grad.addColorStop(1, "rgba(232, 182, 92, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(250, 240, 210, ${p.o})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(tick);
    };
    tick();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      aria-hidden="true"
    />
  );
};
